import React, { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { Youtube } from '@tiptap/extension-youtube';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { FontFamily } from '@tiptap/extension-font-family';
import { Extension } from '@tiptap/core';

// Custom fontSize extension
const FontSize = Extension.create({
  name: 'fontSize',

  addOptions() {
    return {
      types: ['textStyle'],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize.replace(/['"]+/g, ''),
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize: fontSize => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize })
          .run();
      },
      unsetFontSize: () => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize: null })
          .removeEmptyTextStyle()
          .run();
      },
    };
  },
});
import { 
  Bold, Italic, Strikethrough, Code, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Undo, Redo, Link as LinkIcon, Image as ImageIcon,
  Youtube as YoutubeIcon, Table as TableIcon, AlignLeft, AlignCenter, AlignRight,
  AlignJustify, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MenuBar = ({ editor, onImageUpload }) => {
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');

  if (!editor) {
    return null;
  }

  const addLink = () => {
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
      setLinkUrl('');
      setShowLinkDialog(false);
    }
  };

  const addImage = () => {
    if (imageUrl) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      setImageUrl('');
      setShowImageDialog(false);
    }
  };

  const addVideo = () => {
    if (videoUrl) {
      editor.commands.setYoutubeVideo({
        src: videoUrl,
      });
      setVideoUrl('');
      setShowVideoDialog(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const url = await onImageUpload(file);
        if (url) {
          editor.chain().focus().setImage({ src: url }).run();
        }
      } catch (error) {
        console.error('Error uploading image:', error);
      }
    }
  };

  return (
    <div className="border-b border-gray-300 p-2 flex flex-wrap gap-1 bg-gray-50">
      {/* Text Formatting */}
      <Button
        size="sm"
        variant={editor.isActive('bold') ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title="Bold"
      >
        <Bold className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('italic') ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title="Italic"
      >
        <Italic className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('strike') ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        title="Strikethrough"
      >
        <Strikethrough className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('code') ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleCode().run()}
        title="Code"
      >
        <Code className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Headings */}
      <Button
        size="sm"
        variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        title="Heading 1"
      >
        <Heading1 className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        title="Heading 2"
      >
        <Heading2 className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('heading', { level: 3 }) ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        title="Heading 3"
      >
        <Heading3 className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Lists */}
      <Button
        size="sm"
        variant={editor.isActive('bulletList') ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title="Bullet List"
      >
        <List className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('orderedList') ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title="Numbered List"
      >
        <ListOrdered className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('blockquote') ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        title="Quote"
      >
        <Quote className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Alignment */}
      <Button
        size="sm"
        variant={editor.isActive({ textAlign: 'left' }) ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        title="Align Left"
      >
        <AlignLeft className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive({ textAlign: 'center' }) ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        title="Align Center"
      >
        <AlignCenter className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive({ textAlign: 'right' }) ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        title="Align Right"
      >
        <AlignRight className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive({ textAlign: 'justify' }) ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        title="Justify"
      >
        <AlignJustify className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Media */}
      <Button
        size="sm"
        variant="outline"
        onClick={() => setShowLinkDialog(true)}
        title="Add Link"
      >
        <LinkIcon className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setShowImageDialog(true)}
        title="Add Image"
      >
        <ImageIcon className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setShowVideoDialog(true)}
        title="Add Video"
      >
        <YoutubeIcon className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        title="Insert Table"
      >
        <TableIcon className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Undo/Redo */}
      <Button
        size="sm"
        variant="outline"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo"
      >
        <Undo className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo"
      >
        <Redo className="w-4 h-4" />
      </Button>

      {/* Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <input
              type="url"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              onKeyDown={(e) => e.key === 'Enter' && addLink()}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowLinkDialog(false)}>Cancel</Button>
              <Button onClick={addLink}>Add Link</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Dialog */}
      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Image URL</label>
              <input
                type="url"
                placeholder="https://example.com/image.jpg"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                onKeyDown={(e) => e.key === 'Enter' && addImage()}
              />
            </div>
            <div className="text-center text-sm text-gray-500">OR</div>
            <div>
              <label className="block text-sm font-medium mb-2">Upload Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="w-full"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowImageDialog(false)}>Cancel</Button>
              <Button onClick={addImage} disabled={!imageUrl}>Add from URL</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Dialog */}
      <Dialog open={showVideoDialog} onOpenChange={setShowVideoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Video</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <input
              type="url"
              placeholder="YouTube or Vimeo URL"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              onKeyDown={(e) => e.key === 'Enter' && addVideo()}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowVideoDialog(false)}>Cancel</Button>
              <Button onClick={addVideo}>Add Video</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const RichTextEditor = ({ content, onChange, onImageUpload, placeholder = 'Start typing...' }) => {
  const [showBubbleLink, setShowBubbleLink] = useState(false);
  const [showBubbleImage, setShowBubbleImage] = useState(false);
  const [showBubbleVideo, setShowBubbleVideo] = useState(false);
  const [bubbleLinkUrl, setBubbleLinkUrl] = useState('');
  const [bubbleImageUrl, setBubbleImageUrl] = useState('');
  const [bubbleVideoUrl, setBubbleVideoUrl] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      FontFamily,
      FontSize,
      Image.configure({
        inline: true,
        allowBase64: false, // Disabled to prevent large payloads - images must be uploaded first
        HTMLAttributes: {
          class: 'rounded-lg cursor-pointer',
        },
      }).extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            width: {
              default: null,
              parseHTML: element => {
                // Parse width from style or width attribute
                const style = element.getAttribute('style');
                if (style && style.includes('width:')) {
                  const match = style.match(/width:\s*(\d+)px/);
                  if (match) {
                    console.log('Parsed width from HTML:', match[1]);
                    return parseInt(match[1]);
                  }
                }
                const width = element.getAttribute('width');
                if (width) {
                  console.log('Parsed width from attribute:', width);
                  return parseInt(width);
                }
                return null;
              },
              renderHTML: attributes => {
                if (!attributes.width) return {};
                console.log('Rendering image with width:', attributes.width);
                return { 
                  style: `width: ${attributes.width}px; height: auto;`
                };
              },
            },
          };
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline',
        },
      }),
      Youtube.configure({
        controls: true,
        nocookie: true,
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      console.log('Editor content updated, length:', html.length);
      onChange(html);
    },
    editorProps: {
      handlePaste: async (view, event, slice) => {
        // Check if pasted content contains images
        const items = Array.from(event.clipboardData?.items || []);
        const imageItems = items.filter(item => item.type.indexOf('image') !== -1);
        
        if (imageItems.length > 0 && onImageUpload) {
          event.preventDefault();
          
          for (const item of imageItems) {
            const file = item.getAsFile();
            if (file) {
              const url = await onImageUpload(file);
              if (url) {
                editor.chain().focus().setImage({ src: url }).run();
              }
            }
          }
          return true;
        }
        return false;
      },
      handleDrop: async (view, event, slice, moved) => {
        // Check if dropped content contains images
        const files = Array.from(event.dataTransfer?.files || []);
        const imageFiles = files.filter(file => file.type.indexOf('image') !== -1);
        
        if (imageFiles.length > 0 && onImageUpload) {
          event.preventDefault();
          
          for (const file of imageFiles) {
            const url = await onImageUpload(file);
            if (url) {
              editor.chain().focus().setImage({ src: url }).run();
            }
          }
          return true;
        }
        return false;
      },
    },
  });

  // Sync content when prop changes
  React.useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  // Add image resize functionality with visual handle
  React.useEffect(() => {
    if (!editor) return;

    let selectedImage = null;
    let resizeOverlay = null;
    let isResizing = false;
    let startX, startWidth;

    const showResizeOverlay = (img) => {
      console.log('Creating resize overlay for image');
      
      // Remove existing overlay
      if (resizeOverlay) {
        resizeOverlay.remove();
      }

      // Add green border directly to image
      img.style.outline = '3px solid #059669';
      img.style.outlineOffset = '2px';

      // Get image position
      const rect = img.getBoundingClientRect();
      const editorRect = editor.view.dom.getBoundingClientRect();
      
      console.log('Image rect:', rect.width, rect.height, rect.left, rect.top);
      console.log('Editor rect:', editorRect.left, editorRect.top);

      // Create resize handle with fixed positioning
      const handle = document.createElement('div');
      handle.className = 'resize-handle';
      handle.style.cssText = `
        position: fixed;
        left: ${rect.right - 8}px;
        top: ${rect.bottom - 8}px;
        width: 20px;
        height: 20px;
        background: #059669;
        border: 3px solid white;
        border-radius: 50%;
        cursor: nwse-resize;
        z-index: 9999;
        box-shadow: 0 2px 8px rgba(0,0,0,0.4);
      `;

      document.body.appendChild(handle);
      resizeOverlay = handle;

      console.log('Handle position:', handle.style.left, handle.style.top);
      console.log('Handle in DOM:', document.body.contains(handle));

      // Update handle position on scroll
      const updateHandlePosition = () => {
        const rect = img.getBoundingClientRect();
        handle.style.left = `${rect.right - 8}px`;
        handle.style.top = `${rect.bottom - 8}px`;
      };

      // Add mousedown to handle
      handle.onmousedown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('Resize handle mousedown');
        isResizing = true;
        startX = e.clientX;
        startWidth = img.width;
        document.body.style.cursor = 'nwse-resize';
      };

      // Listen for scroll to update position
      editor.view.dom.addEventListener('scroll', updateHandlePosition);
      window.addEventListener('scroll', updateHandlePosition);

      // Store cleanup function
      handle._cleanup = () => {
        editor.view.dom.removeEventListener('scroll', updateHandlePosition);
        window.removeEventListener('scroll', updateHandlePosition);
      };

      console.log('Overlay created and appended to body');
    };

    const hideResizeOverlay = () => {
      if (resizeOverlay) {
        if (resizeOverlay._cleanup) {
          resizeOverlay._cleanup();
        }
        resizeOverlay.remove();
        resizeOverlay = null;
      }
      if (selectedImage) {
        selectedImage.style.outline = '';
        selectedImage.style.outlineOffset = '';
        selectedImage = null;
      }
    };

    const handleClick = (e) => {
      console.log('Click detected on:', e.target.tagName);
      
      if (e.target.tagName === 'IMG' && e.target.closest('.ProseMirror')) {
        console.log('Image clicked!');
        hideResizeOverlay();
        selectedImage = e.target;
        showResizeOverlay(selectedImage);
      } else if (!e.target.classList.contains('resize-handle')) {
        console.log('Click outside image, hiding overlay');
        hideResizeOverlay();
      }
    };

    const handleMouseMove = (e) => {
      if (isResizing && selectedImage) {
        const deltaX = e.clientX - startX;
        const newWidth = Math.max(50, startWidth + deltaX);
        selectedImage.style.width = `${newWidth}px`;
        selectedImage.style.height = 'auto';
        
        // Update handle position
        if (resizeOverlay) {
          const rect = selectedImage.getBoundingClientRect();
          resizeOverlay.style.left = `${rect.right - 8}px`;
          resizeOverlay.style.top = `${rect.bottom - 8}px`;
        }
      }
    };

    const handleMouseUp = () => {
      if (isResizing && selectedImage) {
        // Get the actual displayed width (not natural width)
        const displayedWidth = selectedImage.getBoundingClientRect().width;
        console.log('Resize complete, new width:', displayedWidth);
        isResizing = false;
        document.body.style.cursor = '';
        
        // Update editor with the new width
        const { view } = editor;
        const pos = view.posAtDOM(selectedImage, 0);
        if (pos !== null && pos !== -1) {
          const node = view.state.doc.nodeAt(pos);
          if (node) {
            const tr = view.state.tr.setNodeMarkup(pos, null, {
              ...node.attrs,
              width: Math.round(displayedWidth),
            });
            view.dispatch(tr);
            console.log('Width saved to editor:', Math.round(displayedWidth));
          }
        }
      }
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener('click', handleClick);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      hideResizeOverlay();
      editorElement.removeEventListener('click', handleClick);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [editor]);

  const handleBubbleAddLink = () => {
    if (bubbleLinkUrl) {
      editor.chain().focus().setLink({ href: bubbleLinkUrl }).run();
      setBubbleLinkUrl('');
      setShowBubbleLink(false);
    }
  };

  const handleBubbleAddImage = async () => {
    if (bubbleImageUrl) {
      editor.chain().focus().setImage({ src: bubbleImageUrl }).run();
      setBubbleImageUrl('');
      setShowBubbleImage(false);
    }
  };

  const handleBubbleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file && onImageUpload) {
      const url = await onImageUpload(file);
      if (url) {
        editor.chain().focus().setImage({ src: url }).run();
        setShowBubbleImage(false);
      }
    }
  };

  const handleBubbleAddVideo = () => {
    if (bubbleVideoUrl) {
      editor.commands.setYoutubeVideo({ src: bubbleVideoUrl });
      setBubbleVideoUrl('');
      setShowBubbleVideo(false);
    }
  };

  // Custom floating toolbar state
  const [showFloatingMenu, setShowFloatingMenu] = useState(false);
  const [floatingMenuPosition, setFloatingMenuPosition] = useState({ top: 0, left: 0 });
  const floatingMenuRef = useRef(null);

  // Show/hide floating menu on text selection
  useEffect(() => {
    if (!editor) return;

    const updateFloatingMenu = () => {
      const { selection } = editor.state;
      const { from, to } = selection;
      
      // Show menu only if text is selected
      if (from !== to) {
        const { view } = editor;
        const start = view.coordsAtPos(from);
        const end = view.coordsAtPos(to);
        
        const editorRect = view.dom.getBoundingClientRect();
        const top = start.top - editorRect.top - 60; // 60px above selection
        const left = (start.left + end.left) / 2 - editorRect.left - 150; // Center, offset by half menu width
        
        setFloatingMenuPosition({ top, left });
        setShowFloatingMenu(true);
      } else {
        setShowFloatingMenu(false);
        setShowBubbleLink(false);
        setShowBubbleImage(false);
        setShowBubbleVideo(false);
      }
    };

    editor.on('selectionUpdate', updateFloatingMenu);
    editor.on('update', updateFloatingMenu);

    return () => {
      editor.off('selectionUpdate', updateFloatingMenu);
      editor.off('update', updateFloatingMenu);
    };
  }, [editor]);

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden relative">
      <MenuBar editor={editor} onImageUpload={onImageUpload} />
      
      {/* Custom Floating Menu - Appears on text selection */}
      {editor && showFloatingMenu && (
        <div
          ref={floatingMenuRef}
          style={{
            position: 'absolute',
            top: `${floatingMenuPosition.top}px`,
            left: `${floatingMenuPosition.left}px`,
            zIndex: 50,
          }}
          className="flex items-center gap-1 bg-slate-800 text-white p-2 rounded-lg shadow-xl border border-slate-600"
        >
          {/* Font Size */}
          <select
            onChange={(e) => {
              const size = e.target.value;
              if (size === 'normal') {
                editor.chain().focus().unsetFontSize().run();
              } else {
                editor.chain().focus().setFontSize(size).run();
              }
            }}
            className="px-2 py-1 bg-slate-700 text-white text-sm rounded border border-slate-600"
            title="Font Size"
          >
            <option value="normal">Size</option>
            <option value="12px">12px</option>
            <option value="14px">14px</option>
            <option value="16px">16px</option>
            <option value="18px">18px</option>
            <option value="24px">24px</option>
            <option value="32px">32px</option>
          </select>

          {/* Bold */}
          <Button
            size="sm"
            variant={editor.isActive('bold') ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().toggleBold().run()}
            className="h-8 w-8 p-0"
          >
            <Bold className="w-4 h-4" />
          </Button>

          {/* Italic */}
          <Button
            size="sm"
            variant={editor.isActive('italic') ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className="h-8 w-8 p-0"
          >
            <Italic className="w-4 h-4" />
          </Button>

          <div className="w-px h-6 bg-slate-600 mx-1" />

          {/* Add Link */}
          {!showBubbleLink ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowBubbleLink(true)}
              className="h-8 w-8 p-0"
            >
              <LinkIcon className="w-4 h-4" />
            </Button>
          ) : (
            <div className="flex items-center gap-1">
              <input
                type="url"
                placeholder="https://..."
                value={bubbleLinkUrl}
                onChange={(e) => setBubbleLinkUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleBubbleAddLink()}
                className="px-2 py-1 text-sm bg-slate-700 text-white rounded border border-slate-600 w-48"
                autoFocus
              />
              <Button size="sm" onClick={handleBubbleAddLink} className="h-7 px-2">
                Add
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowBubbleLink(false)} className="h-7 px-2">
                <X className="w-3 h-3" />
              </Button>
            </div>
          )}

          {/* Add Image */}
          {!showBubbleImage ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowBubbleImage(true)}
              className="h-8 w-8 p-0"
            >
              <ImageIcon className="w-4 h-4" />
            </Button>
          ) : (
            <div className="flex items-center gap-1">
              <input
                type="url"
                placeholder="Image URL"
                value={bubbleImageUrl}
                onChange={(e) => setBubbleImageUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleBubbleAddImage()}
                className="px-2 py-1 text-sm bg-slate-700 text-white rounded border border-slate-600 w-32"
              />
              <input
                type="file"
                accept="image/*"
                onChange={handleBubbleImageUpload}
                className="hidden"
                id="bubble-image-upload"
              />
              <label 
                htmlFor="bubble-image-upload"
                className="px-2 py-1 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded border border-slate-600 cursor-pointer transition-colors"
              >
                Upload
              </label>
              <Button size="sm" onClick={handleBubbleAddImage} className="h-7 px-2">
                Add
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowBubbleImage(false)} className="h-7 px-2">
                <X className="w-3 h-3" />
              </Button>
            </div>
          )}

          {/* Add Video */}
          {!showBubbleVideo ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowBubbleVideo(true)}
              className="h-8 w-8 p-0"
            >
              <YoutubeIcon className="w-4 h-4" />
            </Button>
          ) : (
            <div className="flex items-center gap-1">
              <input
                type="url"
                placeholder="YouTube URL"
                value={bubbleVideoUrl}
                onChange={(e) => setBubbleVideoUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleBubbleAddVideo()}
                className="px-2 py-1 text-sm bg-slate-700 text-white rounded border border-slate-600 w-48"
              />
              <Button size="sm" onClick={handleBubbleAddVideo} className="h-7 px-2">
                Add
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowBubbleVideo(false)} className="h-7 px-2">
                <X className="w-3 h-3" />
              </Button>
            </div>
          )}
        </div>
      )}

      <EditorContent 
        editor={editor} 
        className="prose max-w-none p-4 min-h-[300px] focus:outline-none"
        placeholder={placeholder}
      />
      <style jsx>{`
        .ProseMirror {
          min-height: 300px;
          outline: none;
          white-space: pre-wrap;
        }
        .ProseMirror p {
          margin: 0.75em 0;
          line-height: 1.6;
        }
        .ProseMirror p:empty::before {
          content: '\\00a0';
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #adb5bd;
          pointer-events: none;
          height: 0;
        }
        .ProseMirror h1, .ProseMirror h2, .ProseMirror h3 {
          margin: 1.5em 0 0.5em 0;
          line-height: 1.3;
        }
        .ProseMirror h1 {
          font-size: 2em;
          font-weight: bold;
        }
        .ProseMirror h2 {
          font-size: 1.5em;
          font-weight: bold;
        }
        .ProseMirror h3 {
          font-size: 1.25em;
          font-weight: bold;
        }
        .ProseMirror ul, .ProseMirror ol {
          margin: 1em 0;
          padding-left: 2em;
        }
        .ProseMirror li {
          margin: 0.25em 0;
        }
        .ProseMirror img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1rem 0;
          cursor: pointer;
          transition: all 0.2s;
        }
        .ProseMirror img:hover {
          box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.3);
        }
        .ProseMirror .image-wrapper {
          position: relative;
          display: inline-block;
          margin: 1rem 0;
        }
        .ProseMirror .image-wrapper img {
          display: block;
          margin: 0;
        }
        .image-resize-handle {
          transition: transform 0.2s;
        }
        .image-resize-handle:hover {
          transform: scale(1.2);
        }
        .ProseMirror table {
          border-collapse: collapse;
          width: 100%;
          margin: 1rem 0;
        }
        .ProseMirror table td,
        .ProseMirror table th {
          border: 1px solid #ddd;
          padding: 0.5rem;
          min-width: 100px;
        }
        .ProseMirror table th {
          background-color: #f3f4f6;
          font-weight: 600;
        }
        .ProseMirror iframe {
          max-width: 100%;
          aspect-ratio: 16/9;
          margin: 1rem 0;
        }
      `}</style>
      <style>{`
        /* Global styles for bubble menu */
        .tippy-box[data-theme~='dark'] {
          background-color: #1e293b;
          border: 1px solid #475569;
        }
        .tippy-box[data-theme~='dark'] .tippy-arrow {
          color: #1e293b;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
